import React from "react";
import { useForm } from "react-hook-form";
import { omit, orderBy, compact } from "lodash";
import { useHistory } from "react-router";

import useViewer from "../hooks/useViewer";

import useTranslations from "../hooks/useTranslations";
import useInsertTemplate from "../hooks/useInsertTemplate";
import useAddNotification from "../hooks/useAddNotification";
import { GET_TEMPLATES } from "../hooks/useGetTemplates";

import TemplateForm from "../components/templates/TemplateForm";
import { InsertTemplateMutationVariables } from "../graphql/types";

import { Container } from "../components/wombat-ui";

export const DEFAULT_TEMPLATE = {
  title: "",
  templateSections: [],
  taskTrackingEnabled: false,
  color: "#ED64A6",
  contractTemplateIds: [],
};

const TemplateFormScreen = ({
  template: { templateSections, ...baseTemplate } = DEFAULT_TEMPLATE,
  shouldSelectReferent = false,
  returnUrl = "/templates"
}) => {
  const {
    register,
    unregister,
    setValue,
    getValues,
    handleSubmit,
    control,
    errors,
    formState,
    reset,
  } = useForm<{ templateAttributes: InsertTemplateMutationVariables["input"] }>(
    {
      shouldUnregister: true,
      mode: "onBlur",
      defaultValues: {
        templateAttributes: {
          ...baseTemplate,
          templateSectionsAttributes: orderBy(
            templateSections.map(({ templateQuestions, ...ts }) => ({
              ...ts,
              templateQuestionsAttributes: orderBy(
                templateQuestions,
                "order",
                "asc"
              ),
            })),
            "order",
            "asc"
          ),
        },
      },
    }
  );

  const t = useTranslations();
  const history = useHistory();
  const [insertTemplate] = useInsertTemplate();
  const [addNotification] = useAddNotification();
  const viewer = useViewer();

  const onSubmit = handleSubmit(({ templateAttributes }) => {
    insertTemplate({
      variables: {
        input: {
          id: baseTemplate.id,
          companyId: viewer.company.id,
          color: baseTemplate.color,
          title: templateAttributes.title,
          taskTrackingEnabled: templateAttributes.taskTrackingEnabled,
          contractTemplateIds: templateAttributes.contractTemplateIds,
          templateSectionsAttributes: (
            templateAttributes.templateSectionsAttributes || []
          ).map((section, index) => {
            return {
              ...section,
              order: index,
              templateQuestionsAttributes: (
                section.templateQuestionsAttributes || []
              ).map(({ referentIds, ...tqa }, index) => {
                return {
                  ...omit(tqa, "subject", "defaultValueSchema", "digest"),
                  order: index,
                  expectedTimeUnit: "hours",
                  referentIds: compact(referentIds).join('').match(/.{36}/g) // this is needed because RHF will assume single unchecked checkboxes means 'false'
                };
              }),
            };
          }),
        },
      },
      refetchQueries: [{ query: GET_TEMPLATES, variables: { madId: null } }],
      awaitRefetchQueries: true,
    })
      .then(() => {
        addNotification({
          variables: {
            title: t("templates.notification.success") as string,
            status: "success",
          },
        });

        history.push(returnUrl);
      })
      .catch(() => {
        addNotification({
          variables: {
            title: t("templates.notification.error") as string,
            status: "error",
          },
        });
      });
  });

  return (
    <Container className={`flex-grow w-full`}>
      <form
        id="templateForm"
        className="flex flex-col space-y-4"
        onSubmit={onSubmit}
      >
        <TemplateForm
          defaultValue={baseTemplate}
          register={register}
          unregister={unregister}
          setValue={setValue}
          getValues={getValues}
          control={control}
          formState={formState}
          errors={errors?.templateAttributes}
          shouldSelectReferent={shouldSelectReferent}
          reset={reset}
          name="templateAttributes"
        />
      </form>
    </Container>
  );
};

export default React.memo(TemplateFormScreen);
